import { useEffect, useState } from "react";
import { Play, HelpCircle, X, Terminal } from "lucide-react";
import api from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

let fieldsCache = null;

export function QueryHelp({ onPick }) {
  const { t } = useLang();
  const [help, setHelp] = useState(fieldsCache);
  useEffect(() => {
    if (help) return;
    api.get("/work/query/fields").then((r) => { fieldsCache = r.data; setHelp(r.data); }).catch(() => {});
  }, [help]);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" data-testid="query-help-btn" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"><HelpCircle className="h-3.5 w-3.5" />{t("queryHelp")}</button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[26rem] max-w-[92vw] text-xs space-y-3" data-testid="query-help">
        {!help ? <div className="text-muted-foreground">…</div> : (
          <>
            <div>
              <div className="font-semibold mb-1">{t("queryExamples")}</div>
              <ul className="space-y-1">
                {help.examples.map((ex) => (
                  <li key={ex}><button type="button" data-testid="query-example" onClick={() => onPick?.(ex)} className="font-mono text-[11px] text-left text-[#1E3A8A] hover:underline">{ex}</button></li>
                ))}
              </ul>
            </div>
            <div>
              <div className="font-semibold mb-1">{t("queryFields")}</div>
              <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)] gap-x-3 gap-y-0.5 max-h-56 overflow-y-auto pr-1">
                {help.fields.map((f) => <div key={f.field} className="contents"><span className="font-mono text-[11px]">{f.field}</span><span className="text-muted-foreground font-mono text-[11px] truncate" title={f.example}>{f.example}</span></div>)}
              </div>
            </div>
            <div className="text-muted-foreground">{t("operators")}: <span className="font-mono">{help.operators.join("  ")}</span> · {help.keywords.join(", ")}</div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default function QueryBar({ value, onRun, error, busy, count, placeholder }) {
  const { t } = useLang();
  const [draft, setDraft] = useState(value || "");
  useEffect(() => { setDraft(value || ""); }, [value]);
  const run = () => onRun(draft.trim());
  return (
    <div className="space-y-1.5" data-testid="query-bar">
      <div className="flex items-stretch gap-2">
        <div className="relative flex-1">
          <Terminal className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input data-testid="query-input" value={draft} onChange={(e) => setDraft(e.target.value)} spellCheck={false}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); run(); } }}
            placeholder={placeholder || t("queryPlaceholder")}
            className={`h-9 w-full rounded-md border bg-white pl-8 pr-8 font-mono text-[13px] focus:outline-none focus:ring-2 ${error ? "border-[#FCA5A5] focus:ring-[#FCA5A5]/40" : "focus:ring-[#1E3A8A]/30"}`} />
          {draft && <button type="button" data-testid="query-clear" onClick={() => { setDraft(""); onRun(""); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"><X className="h-3.5 w-3.5" /></button>}
        </div>
        <Button size="sm" className="h-9 bg-[#1E3A8A] hover:bg-[#1c3479]" data-testid="query-run-btn" onClick={run} disabled={busy}><Play className="h-3.5 w-3.5 mr-1" />{t("runQuery")}</Button>
      </div>
      <div className="flex items-center justify-between gap-3 text-xs">
        {error ? <span className="text-[#991B1B]" data-testid="query-error">{error}</span>
          : <span className="text-muted-foreground" data-testid="query-count">{value ? `${count ?? 0} ${t("items")}` : t("queryModeHint")}</span>}
        <QueryHelp onPick={(ex) => { setDraft(ex); onRun(ex); }} />
      </div>
    </div>
  );
}
